import React from 'react';
import { SignaturePad } from '@ark-ui/react/signature-pad';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

/** Co se ukládá do hodnoty formuláře. */
export type SignatureOutput = 'paths' | 'png' | 'svg';

export interface SignaturePadInputProps
  extends BaseInputProps<string[] | string> {
  /** Lokalizovaný popisek mazacího tlačítka. */
  clearLabel?: string;
  /**
   * Formát ukládané hodnoty:
   * - `'paths'` (default): pole SVG cest (`string[]`) — serializovatelné a znovu
   *   vykreslitelné (počáteční hodnota se nasází přes `defaultPaths`).
   * - `'png'` / `'svg'`: base64 data URL obrázku (`string`). Z data URL se
   *   plocha zpět nevykreslí (write-only capture); pro binární upload stačí
   *   data URL na blob převést (`await (await fetch(dataUrl)).blob()`).
   */
  output?: SignatureOutput;
  className?: string;
}

const DATA_URL_TYPE: Record<'png' | 'svg', 'image/png' | 'image/svg+xml'> = {
  png: 'image/png',
  svg: 'image/svg+xml',
};

/**
 * Atomické podpisové pole postavené na Ark UI `SignaturePad`.
 *
 * POZOR — `paths` se NEpředává jako controlled prop: zag `bindable` považuje
 * jakoukoli definovanou `value` za controlled, a v tom režimu je interní
 * `context.set("paths", …)` no-op a `context.get` vrací zpět prop hodnotu. Tah
 * dokončený v `onDrawEnd` by se tak nikdy neuložil (kresba po puštění zmizí,
 * `details.paths` je prázdné). Proto je plocha uncontrolled (`defaultPaths`),
 * machine vlastní `paths` a hodnotu odchytáváme v `onDrawEnd`.
 *
 * RHF drží hodnotu podle `output`: pole SVG cest (`string[]`) nebo base64 data
 * URL (`string`). `ids.label` pojmenuje plochu popiskem z `<FormField />`.
 */
export const SignaturePadInput: React.FC<SignaturePadInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  required,
  describedBy,
  labelId,
  testAttributes,
  clearLabel = 'Smazat podpis',
  output = 'paths',
  className,
}) => {
  const ids = React.useMemo(
    () => ({ hiddenInput: id, label: labelId }),
    [id, labelId],
  );

  // Počáteční tahy nasázíme jen u `paths` výstupu (z data URL plochu neobnovíme).
  // `defaultPaths` čte machine jen při mountu — uncontrolled.
  const defaultPaths = React.useMemo(
    () => (output === 'paths' && Array.isArray(value) ? value : undefined),
    [output, value],
  );

  return (
    <SignaturePad.Root
      name={name}
      ids={ids}
      defaultPaths={defaultPaths}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      onDrawEnd={async (details) => {
        if (output === 'paths') {
          onChange(details.paths);
        } else {
          onChange(await details.getDataUrl(DATA_URL_TYPE[output]));
        }
        // Dokončení tahu = interakce → touched (plocha sama blur negeneruje).
        onBlur();
      }}
      className={clsx(styles.signatureRoot, className)}
      data-invalid={hasError ? '' : undefined}
    >
      <SignaturePad.Control className={styles.signatureControl}>
        <SignaturePad.Segment className={styles.signatureSegment} />
        <SignaturePad.Guide className={styles.signatureGuide} />
        <SignaturePad.ClearTrigger
          className={styles.signatureClear}
          aria-label={clearLabel}
          // Ark vyčistí plochu (api.clear); navíc vynulujeme hodnotu formuláře,
          // jinak by v RHF zůstal starý podpis.
          onClick={() => {
            onChange(output === 'paths' ? [] : '');
            onBlur();
          }}
        >
          <Icon name="x-mark" size={16} />
        </SignaturePad.ClearTrigger>
      </SignaturePad.Control>
      <SignaturePad.HiddenInput
        ref={ref}
        // Nativní hodnota skrytého inputu zrcadlí serializovaný stav (RHF drží
        // skutečnou hodnotu); `value` je u Ark HiddenInput povinné.
        value={testAttributes['data-value']}
        aria-describedby={describedBy}
        {...testAttributes}
      />
    </SignaturePad.Root>
  );
};

SignaturePadInput.displayName = 'SignaturePadInput';
